'use client';

import {
  ChangeEvent,
  FC,
  PropsWithChildren,
  ReactElement,
  Suspense,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Breadcrumb, Link } from '@digital-pulse/react';

import styles from './layout.module.scss';
import { ACCOUNT_URL, URL_PREFIX } from '@/lib/constants';
import { BREADCRUMBS_MAP, IBreadcrumb } from '@/lib/constants/breadcrumbs';
import { sendAdobeLinkTag } from '@/helpers/adobeTagging';
import PageAlerts from './pageAlerts';
import { GlobalSpinningLoader } from '@cvs-web/splash-components';
import { I18nDefaultContext, I18nMessage } from '@cvsdigital_web/react-cmk-i18n';
import { isMobileApp } from '@cvs-web/splash-components/server';
import { getReturnUrl } from '@/lib/utils/utils';
import { useTranslation } from '@/components/translation';

interface LayoutProps {
  title?: string;
  subtitle?: string;
  breadcrumbs?: IBreadcrumb[];
  loading?: boolean;
  isFullWidth?: boolean;
}

const Layout: FC<PropsWithChildren<LayoutProps>> = ({
  title,
  subtitle,
  children,
  loading = false,
  isFullWidth = false,
  ...props
}): ReactElement => {
  const router = useRouter();
  const routePathName = usePathname();
  const [returnUrl] = useState(() => (typeof window !== 'undefined' ? getReturnUrl() || '' : ''));
  const breadcrumbs = props.breadcrumbs || BREADCRUMBS_MAP[routePathName || ''];

  const i18nContext = useContext(I18nDefaultContext);
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);

  // i18n translations for breadcrumbs
  const homeText = useTranslation('breadcrumb.home');
  const accountText = useTranslation('breadcrumb.account');
  const commPrefsText = useTranslation('breadcrumb.commPrefs');
  const textsText = useTranslation('breadcrumb.texts');
  const automatedCallsText = useTranslation('breadcrumb.automatedCalls');
  const emailsText = useTranslation('breadcrumb.emails');
  const mailText = useTranslation('breadcrumb.mail');
  const spinnerTitle = useTranslation('spinner.title');
  const spinnerBody = useTranslation('spinner.body');

  // Translation map for breadcrumb keys
  const breadcrumbTranslations: { [key: string]: string } = {
    'breadcrumb.home': homeText,
    'breadcrumb.account': accountText,
    'breadcrumb.commPrefs': commPrefsText,
    'breadcrumb.texts': textsText,
    'breadcrumb.automatedCalls': automatedCallsText,
    'breadcrumb.emails': emailsText,
    'breadcrumb.mail': mailText,
  };

  const topLevelBreadcrumbs: IBreadcrumb[] = [
    {
      text: homeText,
      href: '/',
      linkTag:
        title === commPrefsText
          ? { link_name: 'custom:my_account:communication_preferences:home_link' }
          : {},
    },
    {
      text: accountText,
      href: ACCOUNT_URL,
      linkTag: {
        link_name:
          title === commPrefsText
            ? 'custom:my_account:communication_preferences:account_link'
            : 'custom:my account:personal information:my account link',
      },
    },
  ];
  const translatedBreadcrumbs = (breadcrumbs || []).map((bc) => ({
    ...bc,
    text: breadcrumbTranslations[bc.text] || bc.text,
  }));
  const linkData: IBreadcrumb[] = [...topLevelBreadcrumbs, ...translatedBreadcrumbs];
  const headerRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headerRef.current?.focus();
  }, []);

  useEffect(() => {
    i18nContext.i18n.loadingTranslations.subscribe((loading) => {
      setIsLoadingTranslations(loading);
    });
  }, [i18nContext]);

  return (
    <>
      <GlobalSpinningLoader
        title={spinnerTitle}
        body={spinnerBody}
        show={loading || isLoadingTranslations}
      />
      <main role="main" className={`mainContent ${styles.container}`} tabIndex={-1} data-test-id="page-main">
        {!isMobileApp() &&
          (returnUrl ? (
            <Link href={returnUrl} isBackLink className={styles.backLink}>
              {<I18nMessage translate='breadcrumb.back' />}
            </Link>
          ) : (
            <div className={styles.breadcrumbsContainer}>
              <Breadcrumb
                linkData={linkData.map((ld) => ({
                  ...ld,
                  href: ld.href || `${URL_PREFIX}${ld.route}`,
                }))}
                // @ts-expect-error - Breadcrumb onClick typing mismatch with ChangeEvent
                onClick={(event: ChangeEvent<HTMLAnchorElement>) => {
                  const innerText = event.target.innerText;
                  const matchingCrumb = linkData.find((i) => i.text === innerText);
                  if (matchingCrumb) {
                    if (matchingCrumb.linkTag) {
                      sendAdobeLinkTag({
                        link_name: matchingCrumb.linkTag.link_name,
                      });
                    }
                    if (matchingCrumb.route) {
                      event.preventDefault();
                      router.push(matchingCrumb.route);
                    }
                  }
                }}
              />
            </div>
          ))}
        <div className={`${styles.contentContainer} ${isFullWidth ? styles.contentContainerFullWidth : ''}`}>
          {title && (
            <h1 className={subtitle ? '' : styles.noSubtitle} tabIndex={-1} aria-live="assertive" ref={headerRef}>
              {title}
            </h1>
          )}
          {subtitle && <p className={styles.subTitle}>{subtitle}</p>}
          <Suspense>
            <PageAlerts />
          </Suspense>
          {children}
        </div>
      </main>
    </>
  );
};

export default Layout;
